import { shouldShowSpiceLevel, SPICE_LEVELS } from './spiceUtils';
import { calculateTotalPrice, formatPrice, parsePrice } from './priceUtils';

/**
 * Generate a hash from selected modifiers for unique cart item identification
 */
export const generateModifierHash = (selectedModifiers: any[]): string => {
  if (!selectedModifiers || selectedModifiers.length === 0) {
    return 'none';
  }

  const modifierString = selectedModifiers
    .map(modifier => {
      const options = modifier.selectedOptions || [];
      return `${modifier.name}:${options.sort().join(',')}`;
    })
    .sort()
    .join('|');

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < modifierString.length; i++) {
    const char = modifierString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
};

/**
 * Generate a unique cart item ID based on item ID, spice level, and modifiers
 */
export const generateCartItemId = (
  itemId: string,
  spiceLevel?: string,
  selectedModifiers?: any[]
): string => {
  const spice = spiceLevel || 'none';
  const modifierHash = generateModifierHash(selectedModifiers || []);
  
  return `${itemId}_${spice}_${modifierHash}`;
};

/**
 * Create a simplified cart item with the new data structure
 */
export const createCartItem = (
  item: any,
  spiceLevel: number | null,
  selectedModifiers: any[],
  quantity: number = 1
) => {
  const spiceObject = shouldShowSpiceLevel(item) && spiceLevel !== null ? 
    SPICE_LEVELS.find(level => level.id === spiceLevel) : null;
  
  const totalPrice = calculateTotalPrice(item.price, selectedModifiers, 1);
  
  return {
    item,
    selected: {
      spice: spiceObject,
      modifiers: selectedModifiers
    },
    totalPrice,
    quantity,
    cartItemId: generateCartItemId(
      item.pk_id.toString(), 
      shouldShowSpiceLevel(item) && spiceLevel !== null ? spiceLevel.toString() : undefined, 
      selectedModifiers
    )
  };
};

/**
 * Create a cart item specifically for builder items (salads, etc.)
 * Correctly calculates total price based on quantity
 */
export const createBuilderCartItem = (
  item: any,
  spiceLevel: number | null,
  selectedModifiers: any[],
  quantity: number = 1
) => {
  const spiceObject = shouldShowSpiceLevel(item) && spiceLevel !== null ? 
    SPICE_LEVELS.find(level => level.id === spiceLevel) : null;
  
  // Correctly uses quantity parameter for price calculation
  const totalPrice = calculateTotalPrice(item.price, selectedModifiers, quantity);
  
  return {
    item,
    selected: {
      spice: spiceObject,
      modifiers: selectedModifiers
    },
    totalPrice,
    quantity,
    cartItemId: generateCartItemId(
      item.pk_id.toString(), 
      shouldShowSpiceLevel(item) && spiceLevel !== null ? spiceLevel.toString() : undefined, 
      selectedModifiers
    )
  };
};

/**
 * Cart item display utilities
 */
export const getCartItemName = (cartItem: any) => cartItem.item.name;
export const getCartItemImage = (cartItem: any) => cartItem.item.image;
export const getCartItemSpiceLevel = (cartItem: any) => cartItem.selected.spice;
export const getCartItemModifiers = (cartItem: any) => cartItem.selected.modifiers;
export const getCartItemTotalPrice = (cartItem: any) => cartItem.totalPrice;

/**
 * Render spice level icons count
 */
export const getSpiceIconCount = (spiceObject: any) => {
  if (!spiceObject || !spiceObject.pepperCount) return 0;
  return spiceObject.pepperCount;
};

/**
 * Format modifiers for display with consistent price formatting
 */
export const formatModifiersForDisplay = (modifiers: any[]) => {
  if (!modifiers || !Array.isArray(modifiers)) return [];
  
  return modifiers
    .filter(modifier => modifier.options && modifier.options.length > 0)
    .map(modifier => {
      const optionNames = modifier.options.map((opt: any) => opt.name).join(', ');
      const totalPrice = modifier.options.reduce((sum: number, opt: any) => sum + (parsePrice(opt.price) || 0), 0);
      
      return {
        name: modifier.name,
        options: optionNames,
        price: totalPrice,
        formattedPrice: formatPrice(totalPrice)
      };
    });
};

/**
 * Format modifiers for display with quantity-based pricing
 */
export const formatModifiersForDisplayWithQuantity = (modifiers: any[], quantity: number) => {
  if (!modifiers || !Array.isArray(modifiers)) return [];
  
  return modifiers
    .filter(modifier => modifier.options && modifier.options.length > 0)
    .map(modifier => {
      const optionNames = modifier.options.map((opt: any) => opt.name).join(', ');
      const totalPrice = modifier.options.reduce((sum: number, opt: any) => sum + (parsePrice(opt.price) || 0), 0);
      const totalPriceWithQuantity = totalPrice * quantity;
      
      return {
        name: modifier.name,
        options: optionNames,
        price: totalPrice,
        totalPriceWithQuantity,
        formattedPrice: formatPrice(totalPriceWithQuantity)
      };
    });
};
